import { useEffect, useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { api, CATEGORIES } from "@/lib/api";
import ProductCard from "@/components/ProductCard";

export default function Shop() {
  const { category } = useParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState("featured");

  useEffect(() => {
    setLoading(true);
    api.get("/products", { params: category ? { category } : {} })
      .then((r) => setProducts(r.data))
      .finally(() => setLoading(false));
  }, [category]);

  const sorted = useMemo(() => {
    const list = [...products];
    if (sort === "price-low") list.sort((a, b) => a.price - b.price);
    else if (sort === "price-high") list.sort((a, b) => b.price - a.price);
    else if (sort === "newest") list.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
    return list;
  }, [products, sort]);

  const current = CATEGORIES.find((c) => c.slug === category);
  const title = current?.label || "Shop All";

  return (
    <div className="bg-[#FFF4E0] min-h-screen" data-testid="shop-page">
      {/* Hero */}
      <section className="border-b border-[#E8D8C8]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 py-16 md:py-24">
          <div className="overline mb-3">The Bu&apos;tik</div>
          <h1 className="font-serif-display text-5xl md:text-7xl text-[#1A0F11]">{title}</h1>
          <p className="text-[#5C4A4D] mt-4 max-w-xl">
            {current
              ? `Premium ${current.label.toLowerCase()} curated by The Hair Bu'tik house. Crown yourself.`
              : "Every crown in the house. Wigs, bundles, frontals, closures and the rituals that keep them glossy."}
          </p>
        </div>
      </section>

      {/* Filter bar */}
      <section className="sticky top-[88px] z-30 bg-[#FFF4E0]/90 backdrop-blur-md border-b border-[#E8D8C8]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 py-4 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-2 overflow-x-auto no-scrollbar">
            <Link
              to="/shop"
              className={`text-[11px] uppercase tracking-[0.22em] px-3 py-2 whitespace-nowrap ${!category ? "text-[#FFF4E0] bg-[#831132]" : "text-[#1A0F11] hover:text-[#831132]"}`}
              data-testid="filter-all"
            >
              All
            </Link>
            {CATEGORIES.map((c) => (
              <Link
                key={c.slug}
                to={`/shop/${c.slug}`}
                className={`text-[11px] uppercase tracking-[0.22em] px-3 py-2 whitespace-nowrap ${category === c.slug ? "text-[#FFF4E0] bg-[#831132]" : "text-[#1A0F11] hover:text-[#831132]"}`}
                data-testid={`filter-${c.slug}`}
              >
                {c.label}
              </Link>
            ))}
          </div>
          <div className="flex items-center gap-3">
            <label className="overline text-[#5C4A4D]" htmlFor="sort">Sort</label>
            <select
              id="sort"
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              className="bg-transparent border border-[#E8D8C8] text-sm px-3 py-2 outline-none focus:border-[#831132]"
              data-testid="sort-select"
            >
              <option value="featured">Featured</option>
              <option value="newest">Newest</option>
              <option value="price-low">Price: Low to High</option>
              <option value="price-high">Price: High to Low</option>
            </select>
          </div>
        </div>
      </section>

      {/* Grid */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 py-12 md:py-16">
        {loading ? (
          <p className="text-center text-[#5C4A4D] py-20">Loading...</p>
        ) : sorted.length === 0 ? (
          <div className="text-center py-20">
            <p className="font-serif-display text-3xl text-[#1A0F11]">Nothing here yet.</p>
            <p className="text-sm text-[#5C4A4D] mt-2">New crowns coming soon.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-12" data-testid="product-grid">
            {sorted.map((p) => (
              <ProductCard key={p.product_id} product={p} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
